import { createPendingChatUpload } from "@albertine/chat-uploads";
import { parseChatHostMessageData } from "@albertine/types";
import { useEffect, useEffectEvent, useState } from "react";
import { parentOriginFromLocation } from "@/lib/parentFrame";
import type { ChatOutboxDraft } from "../-data/chatOutbox";
import { registerChatOutboxFile } from "../-data/chatOutboxFiles";
import { assertNever } from "../-utils/chatErrors";
import {
    plainTextToMessageHtml,
    taggedContextToAttachments,
    taggedContextToConfirmedBookings,
} from "../-utils/taggedContext";

type UseHostComposerOptions = {
    scopeKey: string;
    sendDraft: (draft: ChatOutboxDraft) => void;
    onComposerActiveChange: (isActive: boolean) => void;
};

export function useHostComposer({
    scopeKey,
    sendDraft,
    onComposerActiveChange,
}: UseHostComposerOptions) {
    const [composerHeight, setComposerHeight] = useState(0);
    const handleActiveChange = useEffectEvent(onComposerActiveChange);
    const handleSendDraft = useEffectEvent(sendDraft);

    useEffect(
        function listenForHostComposerMessages() {
            const parentOrigin = parentOriginFromLocation();

            function receiveMessage(event: MessageEvent<unknown>) {
                if (!acceptsChatHostEvent(event, parentOrigin)) {
                    return;
                }

                const message = parseChatHostMessageData(event.data);
                if (!message) {
                    return;
                }

                switch (message.type) {
                    case "chat.composer.state":
                        setComposerHeight(message.height);
                        handleActiveChange(message.isActive);
                        return;
                    case "chat.draft.submit":
                        void submitDraftFromHost({
                            message: message.message,
                            files: message.files,
                            reply: message.reply,
                            context: message.context,
                            scopeKey,
                            sendDraft: handleSendDraft,
                        });
                        return;
                    default:
                        return assertNever(message);
                }
            }

            window.addEventListener("message", receiveMessage);

            return () => {
                window.removeEventListener("message", receiveMessage);
            };
        },
        [scopeKey],
    );

    return { composerHeight };
}

export function acceptsChatHostEvent(
    event: Pick<MessageEvent, "origin" | "source">,
    parentOrigin: string | null,
): boolean {
    if (
        window.isIosNativeWebView &&
        event.source === window &&
        event.origin === window.location.origin
    ) {
        return true;
    }

    return (
        parentOrigin !== null &&
        event.origin === parentOrigin &&
        event.source === window.parent
    );
}

async function submitDraftFromHost({
    message,
    files,
    reply,
    context,
    scopeKey,
    sendDraft,
}: {
    message: string;
    files: File[];
    reply?: ChatOutboxDraft["reply"];
    context?: Parameters<typeof taggedContextToAttachments>[0];
    scopeKey: string;
    sendDraft: (draft: ChatOutboxDraft) => void;
}) {
    const uploads = await Promise.all(
        files.map((file) => createPendingChatUpload(file)),
    );

    for (const pending of uploads) {
        registerChatOutboxFile(pending.upload.localId, pending.file);
    }

    sendDraft({
        sendingId: crypto.randomUUID(),
        scopeKey,
        message,
        messageHtml: message ? plainTextToMessageHtml(message) : "",
        attachments: [
            ...taggedContextToAttachments(context),
            ...uploads.map((pending) => pending.attachment),
        ],
        uploads: uploads.map((pending) => pending.upload),
        confirmedBookings: taggedContextToConfirmedBookings(context),
        reply,
    });
}
